import { Nav } from "@/components/prometheus/Nav";
import { Footer } from "@/components/prometheus/Footer";
import { ProjectLifecycleStudio } from "@/components/prometheus/ProjectLifecycleStudio";
import { EcoSmartHomeOS } from "@/components/prometheus/EcoSmartHomeOS";
import { PrometheusSpatialHouseStudio } from "@/components/prometheus/PrometheusSpatialHouseStudio";
import { ArchitectureSuite } from "@/components/prometheus/ArchitectureSuite";
import { EngineeringCADHub } from "@/components/prometheus/EngineeringCADHub";
import { WorkshopVoiceAssistant } from "@/components/prometheus/WorkshopVoiceAssistant";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Layers, Home, Brain, Camera } from "lucide-react";

type MasterStation = "projects" | "university" | "dreamhouse" | "copilot";
type ProjectSubView = "phases" | "cad3d" | "wiring3d" | "university" | "reverse" | "journal";
type DreamHouseSubTab = "spatial3d" | "structural_cfd" | "ecosmart_agro";

const STATIONS = new Set<MasterStation>(["projects", "university", "dreamhouse", "copilot"]);
const PROJECT_VIEWS = new Set<ProjectSubView>(["phases", "cad3d", "wiring3d", "university", "reverse", "journal"]);

const DREAM_HOUSE_ZONES = [
  {
    id: "nucleo",
    title: "1. Núcleo Habitable Inteligente & Economizador",
    area: "85 m² (915 ft²)",
    budgetEst: "$38,400 USD (Obra Gris + Cerramiento)",
    specs: "Hormigón armado 4000 PSI · Bloque 8\" relleno · Varilla #5 Grado 60 @ 12\" · Aleros 30° anti-succión 175 mph.",
    highlights: [
      "Diseño espacial matemático con mobiliario empotrado multifunción y luz cenital natural.",
      "Orientación bioclimática Norte-Sur con ventilación cruzada que reduce 35% la carga térmica en PR.",
      "Ventanas de impacto Miami-Dade y techo con aislamiento reflectivo."
    ]
  },
  {
    id: "taller",
    title: "2. Taller Maker & Laboratorio de Ingeniería Integrado",
    area: "36 m² (388 ft²)",
    budgetEst: "$11,200 USD (Estructura + Red 240V)",
    specs: "Sub-panel 120/240V 60A · Piso epóxico de alto impacto · Muro acústico aislado de la vivienda.",
    highlights: [
      "Bancada estructural nivelada para CNC, impresoras 3D (PETG-CF/ASA) y estación de soldadura electrónica.",
      "Extracción activa de humos con filtro HEPA/carbón y riel aéreo de energía y aire comprimido.",
      "Acceso de carga amplio hacia el patio para meter materiales largos (perfiles, madera, bloques)."
    ]
  },
  {
    id: "agro",
    title: "3. Patio Productivo, Agricultura & Hidropónicos",
    area: "60 m² (645 ft²)",
    budgetEst: "$3,800 USD (Camas + Riego Venturi Solar)",
    specs: "Torres hidropónicas verticales + Camas de cultivo elevadas + Inyector de oxígeno Venturi sin piezas móviles.",
    highlights: [
      "Riego automatizado por sensores capacitivos ESP32 alimentado por el agua de lluvia de la cisterna.",
      "Aprovechamiento de aguas grises biofiltradas para árboles frutales resistentes a viento.",
      "Pérgola solar permeable que da sombra parcial a cultivos tropicales y terraza."
    ]
  },
  {
    id: "energia",
    title: "4. Cuarto Técnico Autosuficiente (Solar + Cisterna)",
    area: "14 m² (150 ft²)",
    budgetEst: "$12,500 USD (Off-Grid Solar + Pluvial)",
    specs: "12 Placas 450W (5.4 kWp) con anclaje Inox 316 · 15–20 kWh LiFePO4 48V · Cisterna 1,200 Gal.",
    highlights: [
      "Transferencia automática en < 15 ms ante apagones de LUMA y monitoreo BMS por RS485/ESP32.",
      "Techo recolector pluvial con desviador de primeras aguas y triple filtrado (sedimentos + carbón + UV).",
      "Autonomía real calculada: 7 días eléctricos sin sol y 21 días de reserva de agua potable."
    ]
  }
];

const WorkshopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stationParam = searchParams.get("station");
  const viewParam = searchParams.get("view");
  const initialStation: MasterStation =
    stationParam && STATIONS.has(stationParam as MasterStation) ? (stationParam as MasterStation) : "projects";
  const initialView: ProjectSubView =
    viewParam && PROJECT_VIEWS.has(viewParam as ProjectSubView) ? (viewParam as ProjectSubView) : "phases";

  const [station, setStation] = useState<MasterStation>(initialStation);
  const [projectView, setProjectView] = useState<ProjectSubView>(initialView);
  const [houseSubTab, setHouseSubTab] = useState<DreamHouseSubTab>("spatial3d");
  const [selectedHouseZone, setSelectedHouseZone] = useState<string>("nucleo");

  useEffect(() => {
    document.title = "Prometheus Engineering OS — Universidad Práctica & Taller con IA";
  }, []);

  useEffect(() => {
    const st = searchParams.get("station");
    const vw = searchParams.get("view");
    if (st && STATIONS.has(st as MasterStation)) setStation(st as MasterStation);
    if (vw && PROJECT_VIEWS.has(vw as ProjectSubView)) setProjectView(vw as ProjectSubView);
  }, [searchParams]);

  const openStation = useCallback(
    (next: MasterStation, view?: ProjectSubView) => {
      setStation(next);
      const params = new URLSearchParams(searchParams);
      params.set("station", next);
      if (next === "projects" || next === "university") {
        const resolved: ProjectSubView =
          view ?? (next === "university" ? "university" : projectView === "university" || projectView === "reverse" ? "phases" : projectView);
        setProjectView(resolved);
        params.set("view", resolved);
      } else {
        params.delete("view");
      }
      setSearchParams(params, { replace: true });
    },
    [projectView, searchParams, setSearchParams]
  );

  const activeZoneObj = DREAM_HOUSE_ZONES.find((z) => z.id === selectedHouseZone) || DREAM_HOUSE_ZONES[0];

  return (
    <main className="min-h-screen bg-void text-foreground selection:bg-primary selection:text-primary-foreground relative overflow-hidden">
      {/* Ambient precision glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none -z-10" />

      <Nav />
      <div className="pt-20 space-y-6 pb-16">
        {/* 4 estaciones = mapa del Taller. Las vistas del proyecto viven solo dentro del estudio (una fila). */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-card border border-border p-3 rounded-2xl shadow-xs">
            <div className="flex items-center gap-1.5 p-1 rounded-xl bg-muted/40 border border-border/60 overflow-x-auto no-scrollbar">
              <button
                type="button"
                onClick={() => openStation("projects", "phases")}
                className={`px-3.5 py-2 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  station === "projects"
                    ? "bg-card text-foreground shadow-xs border border-primary/50 font-extrabold"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
              >
                <Layers className="w-4 h-4 text-primary" />
                <span>1. Proyectos, 3D/Cableado & Costos</span>
              </button>

              <button
                type="button"
                onClick={() => openStation("university", "university")}
                className={`px-3.5 py-2 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  station === "university"
                    ? "bg-card text-emerald-400 shadow-xs border border-emerald-500/50 font-extrabold"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
              >
                <Brain className="w-4 h-4 text-emerald-400" />
                <span>2. Universidad Mecánica & Recrear</span>
              </button>

              <button
                type="button"
                onClick={() => openStation("dreamhouse")}
                className={`px-3.5 py-2 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  station === "dreamhouse"
                    ? "bg-card text-cyan-400 shadow-xs border border-cyan-500/50 font-extrabold"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
              >
                <Home className="w-4 h-4 text-cyan-400" />
                <span>3. Casa Soñada Autosuficiente PR</span>
              </button>

              <button
                type="button"
                onClick={() => openStation("copilot")}
                className={`px-3.5 py-2 rounded-lg text-xs font-mono-tech uppercase font-bold transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  station === "copilot"
                    ? "bg-card text-amber-400 shadow-xs border border-amber-500/50 font-extrabold"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/40"
                }`}
              >
                <Camera className="w-4 h-4 text-amber-400" />
                <span>4. Fotogrametría, Voz & Visión</span>
              </button>
            </div>

            <div className="flex items-center justify-between lg:justify-end gap-2 text-xs font-mono-tech text-muted-foreground pr-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Prometheus Engineering OS · MechAgent · Vento · SkillFall</span>
            </div>
          </div>
        </div>

        {/* ======================================================================= */}
        {/* STATION 1 & 2: PROJECTS, 3D CAD/WIRING, UNIVERSITY & REVERSE ENGINEER   */}
        {/* ======================================================================= */}
        {(station === "projects" || station === "university") && (
          <div className="animate-in fade-in duration-200">
            <ProjectLifecycleStudio initialStudioView={projectView} />
          </div>
        )}

        {/* ======================================================================= */}
        {/* STATION 3: ARQUITECTO DE LA CASA SOÑADA AUTOSUFICIENTE EN PUERTO RICO   */}
        {/* ======================================================================= */}
        {station === "dreamhouse" && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6 animate-in fade-in duration-200">
            {/* Master Architectural Vision Header & 4 Zones Selector */}
            <div className="bg-card border border-cyan-500/30 rounded-2xl p-5 space-y-5 shadow-sm font-mono-tech">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-4">
                <div>
                  <span className="text-xs font-bold text-cyan-400 uppercase block">
                    Estudio Arquitectónico & Estructural Integral · Puerto Rico (ASCE 7 · 175+ mph)
                  </span>
                  <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-foreground mt-0.5">
                    Diseño de la Casa Soñada: Inteligente, Eficiente y Autosuficiente
                  </h2>
                  <p className="text-xs text-muted-foreground font-sans mt-1">
                    Unifica construcción sólida en hormigón, optimización matemática del espacio, energía solar LiFePO4, cosecha pluvial, agricultura y tu taller integrado.
                  </p>
                </div>

                {/* Sub-Tool Switcher inside Dream House Station */}
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  <button
                    onClick={() => setHouseSubTab("spatial3d")}
                    className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer border transition-all ${
                      houseSubTab === "spatial3d"
                        ? "bg-cyan-500 text-black border-cyan-400"
                        : "bg-background border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🏠 Dollhouse 3D · Plano 2D · .DXF
                  </button>
                  <button
                    onClick={() => setHouseSubTab("structural_cfd")}
                    className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer border transition-all ${
                      houseSubTab === "structural_cfd"
                        ? "bg-amber-500 text-black border-amber-400"
                        : "bg-background border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🌪️ Estructura & Viento 175 mph (CFD)
                  </button>
                  <button
                    onClick={() => setHouseSubTab("ecosmart_agro")}
                    className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer border transition-all ${
                      houseSubTab === "ecosmart_agro"
                        ? "bg-emerald-500 text-black border-emerald-400"
                        : "bg-background border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🌱 Solar, Cisterna, Huerto & Sensores
                  </button>
                </div>
              </div>

              {/* 4 Integrated Architectural Zones Interactive Selector */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {DREAM_HOUSE_ZONES.map((z) => {
                  const isSel = z.id === selectedHouseZone;
                  return (
                    <button
                      key={z.id}
                      type="button"
                      onClick={() => setSelectedHouseZone(z.id)}
                      className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-2 ${
                        isSel
                          ? "bg-cyan-500/15 border-cyan-400 shadow-sm"
                          : "bg-background border-border hover:border-cyan-500/40"
                      }`}
                    >
                      <div>
                        <span className="text-xs font-bold text-foreground block">{z.title}</span>
                        <span className="text-[11px] text-cyan-400 font-bold">{z.area}</span>
                      </div>
                      <span className="text-[10.5px] text-emerald-400 font-bold">{z.budgetEst}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Zone Engineering Detail Banner */}
              <div className="p-4 rounded-xl bg-background border border-border grid grid-cols-1 lg:grid-cols-12 gap-4 text-xs">
                <div className="lg:col-span-5 space-y-1.5">
                  <span className="text-cyan-400 font-bold uppercase block">
                    Especificación Constructiva de la Zona Activa:
                  </span>
                  <h4 className="text-sm font-bold text-foreground">{activeZoneObj.title}</h4>
                  <p className="text-muted-foreground font-sans leading-relaxed">{activeZoneObj.specs}</p>
                </div>
                <div className="lg:col-span-7 space-y-1.5 font-sans">
                  <span className="font-mono-tech text-emerald-400 font-bold uppercase block">
                    Ingeniería Aplicada, Logística & Ahorro de Espacio:
                  </span>
                  <ul className="space-y-1 text-foreground">
                    {activeZoneObj.highlights.map((h, i) => (
                      <li key={i}>• {h}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Sub-Station Active Module */}
            {houseSubTab === "spatial3d" && <PrometheusSpatialHouseStudio />}
            {houseSubTab === "structural_cfd" && <ArchitectureSuite />}
            {houseSubTab === "ecosmart_agro" && <EcoSmartHomeOS />}
          </div>
        )}

        {/* ======================================================================= */}
        {/* STATION 4: COPILOTO EN VIVO (FOTOGRAMETRÍA, VOZ & VISIÓN)               */}
        {/* ======================================================================= */}
        {station === "copilot" && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6 animate-in fade-in duration-200">
            <div className="bg-card border border-amber-500/30 rounded-2xl p-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3 font-mono-tech">
                <div>
                  <span className="text-xs font-bold text-amber-400 uppercase block">
                    Estación Multimodal Manos Libres · Cámara + Voz + Escaneo 3D
                  </span>
                  <h2 className="font-serif-display text-2xl font-bold text-foreground">
                    Fotogrametría 3D, Copiloto de Voz & Puente CAD en Vivo
                  </h2>
                </div>
              </div>
              <WorkshopVoiceAssistant />
            </div>

            <EngineeringCADHub />
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

export default WorkshopPage;
